import Vue from 'vue';
import _ from 'lodash';
import { getRouteAndParams } from './properties.helpers';
import { lsApiService, lsErrorService, lsTrackingService } from 'ls-vue-common';
import {
  PROPERTIES__RESET,
  PROPERTIES__MUTATE_PROPERTIES,
  PROPERTIES__MUTATE_PROPERTY,
  PROPERTIES__MUTATE_SERVICE_PROMOS,
  PROPERTIES__MUTATE_LOADING_STATUS,
  PROPERTIES__MUTATE_APPLICATION_REVIEW_PROPERTY_ID,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  PROPERTIES__ADD_PROPERTY,
  PROPERTIES__UPDATE_PROPERTY_INFO,
  PROPERTIES__GET_SERVICE_PROMOS,
  PROPERTIES__GET_SERVICE_PRICING,
  PROPERTIES__GET_PROPERTY_ID_FOR_ORDER_SERVICES,
  PROPERTIES__GET_CUSTOMER_LEAD_BY_ID,
  PROPERTIES__GET_FIRST_HIRED_PRO_NEEDING_DETAILS,
  PROPERTIES__PROPERTIES_IN_ARRAY,
  PROPERTIES__PROPERTIES_IN_ARRAY_WITH_ACTIVE_MOWING,
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__HAS_MULTIPLE_PROPERTIES,
  PROPERTIES__CAN_CANCEL_MOWING_SERVICES,
  AUTH__MUTATE_CUSTOMER,
  SCHEDULES__MUTATE_SCHEDULES,
  REQUIRED_SERVICES__MUTATE_REQUIRED_SERVICES,
  RECOMMENDATIONS__MUTATE_RECOMMENDATIONS,
  PROPERTY_MANAGER__SHOW_SINGLE_PROPERTY,
  PROPERTY_MANAGER__CURRENT_PROPERTY_ID,
  PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID,
  PROPERTIES__GET_ELIGIBLE_PROS_FOR_REDISTRIBUTION,
  PROPERTIES__MUTATE_ELIGIBLE_PROS_FOR_REDISTRIBUTION,
  PROPERTIES__REDISTRIBUTE_PRO,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';
import router from '@/router';
import { lsPropertyHelpers } from '@/helpers';

const createInitialState = () => {
  return {
    propertiesById: {},
    servicePromosByPropertyId: {},
    eligibleProsForRedistribution: {
      hasDisputedPros: false,
      hasEligiblePros: false,
      hasOneTimeDayChangePros: false,
      properties: [],
    },
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [PROPERTIES__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [PROPERTIES__MUTATE_PROPERTIES](state, payload) {
    state.propertiesById = _.keyBy(payload, 'property_id');
  },

  [PROPERTIES__MUTATE_PROPERTY](state, payload) {
    const { property_id } = payload;
    Vue.set(state.propertiesById, property_id, {
      ...state.propertiesById[property_id],
      ...payload,
    });
  },

  [PROPERTIES__MUTATE_SERVICE_PROMOS](state, payload) {
    state.servicePromosByPropertyId[payload.property_id] = payload.promos;
  },

  [PROPERTIES__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },

  [PROPERTIES__MUTATE_ELIGIBLE_PROS_FOR_REDISTRIBUTION](state, payload) {
    state.eligibleProsForRedistribution = payload;
  },

  [PROPERTIES__MUTATE_APPLICATION_REVIEW_PROPERTY_ID](state, payload) {
    state.applicationReviewPropertyId = payload;
  },
};

export const actions = {
  [PROPERTIES__REDISTRIBUTE_PRO]({ commit, rootState }, { pro, property, reason, reasonOther }) {
    commit(PROPERTIES__MUTATE_LOADING_STATUS, { isUpdating: true });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property}/contractors/${pro}/redistribute`,
        payload: {
          reason,
          reasonOther,
        },
      })
      .then(() => true)
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
        return false;
      })
      .finally((result) => {
        commit(PROPERTIES__MUTATE_LOADING_STATUS, { isUpdating: false });

        lsTrackingService.track({
          event: 'change_my_pro_is_successful',
          target: 'change_my_pro-result',
          data: {
            ui_component_rendered: result ? 'ChangeProSuccess' : 'ChangeProFail',
            is_change_my_pro_successful: result,
          },
        });

        return result;
      });
  },
  [PROPERTIES__GET_ELIGIBLE_PROS_FOR_REDISTRIBUTION]({ commit, rootState }) {
    commit(PROPERTIES__MUTATE_LOADING_STATUS, { isLoading: true });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'GET',
        route: `/customers/${customer_id}/my-pros`,
      })
      .then(({ data }) => commit(PROPERTIES__MUTATE_ELIGIBLE_PROS_FOR_REDISTRIBUTION, data))
      .catch((error) => lsErrorService.log({ error, showAlert: true }))
      .finally(() => commit(PROPERTIES__MUTATE_LOADING_STATUS, { isLoading: false }));
  },
  async [PROPERTIES__GET_PROPERTIES_AND_SCHEDULES](
    { commit, state, rootState, rootGetters },
    { forceRefresh = false } = {}
  ) {
    const { customer_id } = rootState.auth.user;
    const { propertiesById } = state;

    if (_.isEmpty(propertiesById) || forceRefresh) {
      commit(PROPERTIES__MUTATE_LOADING_STATUS, {
        isLoading: true,
      });

      const propertyID = rootGetters[PROPERTY_MANAGER__CURRENT_PROPERTY_ID];
      const showSingleProperty = rootGetters[PROPERTY_MANAGER__SHOW_SINGLE_PROPERTY];

      // helper function determines the route and params based on if
      // customer is a property manager
      const { route, params } = getRouteAndParams({
        customer_id,
        property_id: showSingleProperty ? propertyID : null,
      });

      return lsApiService
        .request({
          method: 'GET',
          route,
          params,
        })
        .then(({ data }) => {
          const properties = showSingleProperty ? [data] : data.properties;

          const last_completed_scheduleevent = showSingleProperty
            ? {}
            : data.last_completed_scheduleevent;

          commit(AUTH__MUTATE_CUSTOMER, {
            last_completed_scheduleevent,
          });

          commit(PROPERTIES__MUTATE_PROPERTIES, properties);

          const { schedules, requiredServices, recommendations } = _.reduce(
            properties,
            (result, property) => {
              return {
                schedules: _.concat(result.schedules, _.get(property, 'schedules', [])),
                requiredServices: _.concat(
                  result.requiredServices,
                  _.get(property, 'requiredServices', [])
                ),
                recommendations: _.concat(
                  result.recommendations,
                  _.get(property, 'recommendations', [])
                ),
              };
            },
            { schedules: [], requiredServices: [], recommendations: [] }
          );

          commit(SCHEDULES__MUTATE_SCHEDULES, schedules);
          commit(REQUIRED_SERVICES__MUTATE_REQUIRED_SERVICES, requiredServices);
          commit(RECOMMENDATIONS__MUTATE_RECOMMENDATIONS, recommendations);
        })
        .catch((error) => {
          lsErrorService.log({
            error,
            showAlert: true,
          });
        })
        .finally(() => {
          commit(PROPERTIES__MUTATE_LOADING_STATUS, {
            isLoading: false,
          });
        });
    }
    return Promise.resolve();
  },
  [PROPERTIES__ADD_PROPERTY](
    { commit, dispatch, rootState, rootGetters },
    { street, city, state, zip, onSuccess }
  ) {
    commit(PROPERTIES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties`,
        payload: {
          name: street,
          street,
          city,
          state,
          zip,
          country: 'USA',
        },
      })
      .then(({ data }) => {
        commit(PROPERTIES__MUTATE_PROPERTY, data);

        if (rootGetters[PROPERTY_MANAGER__SHOW_SINGLE_PROPERTY]) {
          dispatch(PROPERTY_MANAGER__UPDATE_CURRENT_PROPERTY_ID, { propertyID: data.id });
        }

        if (data.can_self_reactivate) {
          router.push({
            name: 'reactivate',
            params: {
              property_id: data.property_id,
            },
          });
        }
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROPERTIES__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
  [PROPERTIES__UPDATE_PROPERTY_INFO]({ commit, rootState }, { property_id, info, onSuccess }) {
    commit(PROPERTIES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'PUT',
        route: `/customers/${customer_id}/properties/${property_id}`,
        payload: { info },
      })
      .then(({ data }) => {
        commit(PROPERTIES__MUTATE_PROPERTY, {
          property_id: _.toNumber(property_id),
          info: data.info,
        });
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(PROPERTIES__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
  [PROPERTIES__GET_SERVICE_PROMOS]({ commit, state, rootState, rootGetters }) {
    commit(PROPERTIES__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer_id } = rootState.auth.user;

    const propertyID = rootGetters[PROPERTY_MANAGER__CURRENT_PROPERTY_ID];
    const showSingleProperty = rootGetters[PROPERTY_MANAGER__SHOW_SINGLE_PROPERTY];

    if (showSingleProperty) {
      return lsApiService
        .request({
          method: 'GET',
          route: `/customers/${customer_id}/properties/${propertyID}/service-promos`,
        })
        .then(({ data }) => {
          commit(PROPERTIES__MUTATE_SERVICE_PROMOS, {
            property_id: propertyID,
            promos: data,
          });
        })
        .catch((error) => {
          lsErrorService.log({ error });
        })
        .finally(() => {
          commit(PROPERTIES__MUTATE_LOADING_STATUS, {
            isLoading: false,
          });
        });
    }

    const allIds = _.map(state.propertiesById, (__, id) => _.toNumber(id));
    const allProperties = _.map(allIds, (id) => {
      return lsApiService.request({
        method: 'GET',
        route: `/customers/${customer_id}/properties/${id}/service-promos`,
      });
    });

    return Promise.all(allProperties)
      .then((result) => {
        _.forEach(allIds, (id, idx) => {
          commit(PROPERTIES__MUTATE_SERVICE_PROMOS, {
            property_id: id,
            promos: result[idx].data,
          });
        });
      })
      .catch((error) => {
        lsErrorService.log({ error });
      })
      .finally(() => {
        commit(PROPERTIES__MUTATE_LOADING_STATUS, {
          isLoading: false,
        });
      });
  },
  [PROPERTIES__GET_SERVICE_PRICING](
    { rootState },
    { property_id, market_id, service_id, forceRefresh }
  ) {
    if (!market_id) {
      market_id = rootState.properties.propertiesById[property_id].market_id;
    }

    const storedMarketServicePricing = _.get(
      rootState,
      `orderServices.servicesByMarketId[${market_id}][${service_id}].price_ranges[0]`,
      {}
    );

    let requestPromise = Promise.resolve(storedMarketServicePricing);
    if (_.isEmpty(storedMarketServicePricing) || forceRefresh) {
      requestPromise = lsApiService
        .request({
          method: 'GET',
          route: `/markets/${market_id}/services/${service_id}/pricerange`,
        })
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          lsErrorService.log({ error });
          return {};
        });
    }

    return requestPromise;
  },
};

export const getters = {
  [PROPERTIES__PROPERTIES_IN_ARRAY](state) {
    return _.orderBy(
      _.values(state.propertiesById),
      [
        (property) => {
          // prioritize active properties
          return property.is_active;
        },
        (property) => {
          // maintain property creation order in their respective is_active t/f buckets
          return property.property_id;
        },
      ],
      ['desc', 'asc']
    );
  },

  [PROPERTIES__GET_PROPERTY_ID_FOR_ORDER_SERVICES]: (state, getters) => (property_id) => {
    // see if property exists
    const getProperty = _.get(state.propertiesById, `${property_id}`);

    if (getProperty) {
      return getProperty.property_id;
    }

    // if not, find the first property that can order
    const getPropertyThatCanOrder = _.find(
      getters[PROPERTIES__PROPERTIES_IN_ARRAY_WITH_ACTIVE_MOWING],
      (property) => {
        return property.can_request_additional_services;
      }
    );

    if (getPropertyThatCanOrder) {
      return getPropertyThatCanOrder.property_id;
    }

    // else just get the first property in the all properties array
    return _.head(getters[PROPERTIES__PROPERTIES_IN_ARRAY]).property_id;
  },

  [PROPERTIES__PROPERTIES_IN_ARRAY_WITH_ACTIVE_MOWING](state) {
    return _.filter(state.propertiesById, (property) => {
      return !_.isNil(property.active_mowing_schedule_id);
    });
  },

  [PROPERTIES__SELECTED_PROPERTY]: (state) => (property_id) => {
    return state.propertiesById[property_id];
  },

  [PROPERTIES__HAS_MULTIPLE_PROPERTIES](state) {
    return Boolean(_.size(state.propertiesById) > 1);
  },

  [PROPERTIES__GET_CUSTOMER_LEAD_BY_ID]: (state) => (property_id, id) => {
    const selectedProperty = getters[PROPERTIES__SELECTED_PROPERTY](state)(property_id);
    const selectedCustomerLead = _.find(selectedProperty.customer_leads, {
      id: parseInt(id, 10),
    });
    return selectedCustomerLead;
  },

  [PROPERTIES__GET_FIRST_HIRED_PRO_NEEDING_DETAILS](state) {
    let hiredProWithoutDetails = null;
    if (!_.isEmpty(state.propertiesById)) {
      _.forEach(state.propertiesById, (property) => {
        _.forEach(property.customer_leads, (lead) => {
          if (lead.needs_hired_details) {
            const contact = _.find(lead.customer_lead_contacts, { status: 'hired' });
            const contractorProps = {
              name: contact.contractor.name,
              user: _.pick(contact.contractor.user, ['first_name', 'last_name']),
            };

            const servicesString = _.map(lead.services, (service) => {
              return service.name;
            }).join(', ');

            hiredProWithoutDetails = {
              customerLeadId: lead.id,
              customerLeadContact: {
                id: contact.id,
                status: contact.status,
                contractor: { ...contractorProps },
              },
              serviceName: servicesString,
              propertyId: property.id,
              createdAt: lead.created_at,
            };
          }
        });
      });
    }
    return hiredProWithoutDetails || null;
  },

  [PROPERTIES__CAN_CANCEL_MOWING_SERVICES](state) {
    return _.some(_.entries(state.propertiesById), ([, property]) =>
      lsPropertyHelpers.hasCancellableMowing({ property })
    );
  },
};

export const properties = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
